import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import List from 'partial/components/List';
import SearchBar from 'partial/components/input/SearchBar';
import ThumbIcon from 'partial/components/ThumbIcon';
import { head, pickBy } from 'lodash';
import { usePersistState } from 'helper';
import Pagination from 'partial/components/Pagination';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import EmptyState from 'partial/components/EmptyState';
import classNames from 'classnames';
import SubscriberProfile from './components/SubscriberProfile';
import { useOrganizationList } from './hooks';

function SubscribersDashboard() {
  const { setSubLabel } = useModuleContext();
  const [selected, setSelected] = usePersistState('subscriber-id', '', {
    keepOnUnmount: false,
  });
  const [filter, setFilter] = useState({ q: '' });
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
        per_page: 15,
        sort_by: 'name',
        order: 'asc',
      }),
    [filter, page],
  );

  useEffect(() => {
    setPage({ page: 1 });
  }, [filter]);

  const [isLoading, list, meta] = useOrganizationList(payload);

  const navigate = useNavigate();
  const onSubSelect = (item) => {
    setSelected(item?.user?.uuid);
    navigate(`${item?.user?.uuid}`);
  };

  useEffect(() => {
    if (selected) return;
    setSelected(head(list)?.user?.uuid);
  }, [list, selected, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} agenc${meta?.total === 1 ? 'y' : 'ies'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="min-h-[calc(100vh_-_1rem)] flex bg-white border border-neutral-200 rounded-md flex-grow static flex-col md:flex-row overflow-hidden">
      <div className="md:border-r border-neutral-200 w-full md:w-[300px] md:flex-shrink-0 flex flex-col">
        <div className="p-3 border-b border-neutral-200">
          <SearchBar
            name="q"
            value={filter.q}
            onChange={setFilter}
            inputClassName="!w-full"
            containerClassName="!w-full"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          <List
            data={list}
            isLoading={isLoading}
            selectedId={selected}
            onSelectRow={onSubSelect}
            containerClassName="!w-full"
            render={(item) => {
              const name = item?.name; // startCase(capitalize(item?.name));
              return (
                <div className="flex items-center gap-3 px-4 py-3 w-full min-w-0">
                  <ThumbIcon
                    className="h-9 w-9 rounded-full"
                    alt={name}
                    url={item?.user?.profile?.avatar || ''}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{name}</div>
                    <div
                      className={classNames(
                        'text-xs font-semibold',
                        item?.is_banned
                          ? 'text-danger-700'
                          : 'text-success-700',
                      )}
                    >
                      {item?.is_banned ? 'Banned' : 'Active'}
                    </div>
                  </div>
                </div>
              );
            }}
            selectedKey="user.uuid"
            emptyGlyph="results"
            emptyStatement="No agencies match this search."
          />
        </div>
        <div className="p-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between">
          <p className="text-xs text-neutral-500 tabular-nums">
            {list?.length || 0} of {meta?.total || 0}
          </p>
          <Pagination value={meta} onChange={setPage} withPage={false} />
        </div>
      </div>
      <Routes>
        <Route path=":id/*" element={<SubscriberProfile />} />
        {list?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(list)?.user?.uuid}`} />}
          />
        )}
        <Route
          path="*"
          element={
            <div className="flex-1">
              <EmptyState
                variant="detail"
                statement="Select an agency to see its detail."
              />
            </div>
          }
        />
      </Routes>
    </section>
  );
}

export default SubscribersDashboard;
